import clsx from 'clsx';
import type { PlayerView } from '@mafia/core';
import { Avatar } from '@/components/Avatar';
import { Stopwatch, Timer } from './Timer';

/**
 * Анимация утра: сначала итоги ночи (выстрел или «никто не погиб»), затем визит любовницы.
 * Шаг приходит с сервера (view.morningStep), поэтому у всех экранов картинка меняется одновременно;
 * в ручном режиме сменой шагов управляет ведущий — он же решает, сколько длится анимация.
 * inline — уменьшенная копия для экрана ведущего (не перекрывает его кнопки).
 */
export function NightReveal({
  view,
  avatars,
  inline = false,
}: {
  view: PlayerView;
  avatars: Map<string, string | null>;
  inline?: boolean;
}) {
  const step = view.morningStep;
  if (!step) return null;
  const person = (seat: number) => {
    const p = view.players.find((x) => x.seat === seat);
    return { seat, name: p?.nickname ?? `№${seat}`, avatarId: p ? (avatars.get(p.userId) ?? null) : null };
  };

  return (
    <div
      // Ключ по шагу: каждая анимация проигрывается с начала.
      key={`${step}-${view.day}`}
      className={clsx(
        'flex flex-col items-center justify-center overflow-hidden text-center',
        inline ? 'relative mb-3 min-h-[260px] rounded-2xl border border-white/10 px-4 py-8' : 'fixed inset-0 z-40 px-6',
        inline ? 'bg-ink-950' : 'bg-ink-950/95 backdrop-blur-sm',
      )}
      style={step === 'LOVER' ? { backgroundImage: 'radial-gradient(ellipse at center, rgba(131,24,67,.5), transparent 65%)' } : undefined}
      role="status"
      aria-live="polite"
    >
      {step === 'KILL' ? (
        view.lastNightDeaths.length > 0 ? (
          <Kill victims={view.lastNightDeaths.map(person)} withLover={view.lastNightWithLover} inline={inline} />
        ) : (
          <Peace inline={inline} />
        )
      ) : (
        view.lastNightLoverVisit !== null && (
          <Lover
            guest={person(view.lastNightLoverVisit)}
            died={view.lastNightWithLover !== null}
            silenced={view.silencedSeat === view.lastNightLoverVisit}
            inline={inline}
          />
        )
      )}

      {/* У ведущего секундомер уже есть в шапке экрана */}
      {!inline && (
        <div className="absolute bottom-8 flex items-center gap-2 text-xs text-zinc-500">
          {view.pace === 'manual' ? (
            <>
              <Stopwatch since={view.phaseStartedAt} className="text-sm text-zinc-500" />
              <span>· ведущий продолжит игру</span>
            </>
          ) : (
            <Timer endsAt={view.phaseEndsAt} className="text-sm text-zinc-500" />
          )}
        </div>
      )}
    </div>
  );
}

type Person = { seat: number; name: string; avatarId: string | null };

function Kill({ victims, withLover, inline }: { victims: Person[]; withLover: number | null; inline: boolean }) {
  const size = inline ? 64 : 104;
  return (
    <>
      {/* Кровавые подтёки сверху */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-around motion-reduce:hidden" aria-hidden>
        {[38, 70, 52, 90, 44, 64, 30].map((h, i) => (
          <span
            key={i}
            className="block w-2 origin-top animate-drip rounded-b-full bg-gradient-to-b from-blood-700 to-blood-500"
            style={{ height: inline ? h * 0.5 : h * 1.4, animationDelay: `${0.4 + i * 0.12}s` }}
          />
        ))}
      </div>
      {/* Вспышка выстрела */}
      <span className="pointer-events-none absolute h-24 w-24 animate-shot-flash rounded-full bg-gradient-to-br from-white via-gold-300 to-blood-500 motion-reduce:hidden" aria-hidden />

      <p className={clsx('animate-rise font-display uppercase tracking-[.3em] text-blood-400', inline ? 'text-xs' : 'text-sm')}>Город просыпается</p>
      <div className={clsx('flex flex-wrap justify-center gap-6', inline ? 'my-4' : 'my-8')}>
        {victims.map((v) => (
          <div key={v.seat} className="flex animate-victim-in flex-col items-center gap-3 motion-reduce:animate-none">
            <div className="relative animate-shake motion-reduce:animate-none">
              <div className="animate-fade motion-reduce:animate-none">
                <Avatar id={v.avatarId} name={v.name} size={size} />
              </div>
              {/* Перечёркивание: поворот снаружи, «прорисовка» внутри — иначе анимация перебьёт поворот */}
              <span className="absolute left-[-12%] top-1/2 h-1.5 w-[124%] -rotate-[24deg]">
                <span className="block h-full w-full origin-left animate-slash rounded-full bg-blood-500 shadow-[0_0_12px_rgba(220,38,38,.8)]" />
              </span>
            </div>
            <span className={clsx('font-medium text-zinc-100', inline ? 'text-sm' : 'text-lg')}>
              №{v.seat} {v.name}
            </span>
            {v.seat === withLover && <span className="text-xs text-pink-300">вместе с любовницей</span>}
          </div>
        ))}
      </div>
      <h2 className={clsx('animate-rise font-display text-zinc-100', inline ? 'text-xl' : 'text-3xl sm:text-4xl')} style={{ animationDelay: '1.5s' }}>
        Этой ночью {victims.length > 1 ? 'убиты' : 'убит'} {victims.map((v) => `№${v.seat}`).join(' и ')}
      </h2>
    </>
  );
}

function Peace({ inline }: { inline: boolean }) {
  return (
    <>
      {/* Луна уходит, над горизонтом встаёт солнце */}
      <div className={clsx('relative overflow-hidden', inline ? 'mb-4 h-24 w-48' : 'mb-8 h-40 w-80')} aria-hidden>
        <span className={clsx('absolute inset-x-0 top-0 flex animate-moon-out justify-center motion-reduce:hidden', inline ? 'text-4xl' : 'text-6xl')}>🌙</span>
        <span
          className={clsx(
            'absolute bottom-0 left-1/2 -ml-[var(--r)] h-[calc(var(--r)*2)] w-[calc(var(--r)*2)] translate-y-1/2 animate-dawn rounded-full bg-gradient-to-b from-gold-300 via-gold-400 to-orange-500 shadow-[0_0_60px_20px_rgba(233,185,73,.35)] motion-reduce:animate-none',
          )}
          style={{ ['--r' as string]: inline ? '36px' : '60px' }}
        />
        <span className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-300/70 to-transparent" />
      </div>
      <p className={clsx('animate-rise font-display uppercase tracking-[.3em] text-gold-300', inline ? 'text-xs' : 'text-sm')}>Город просыпается</p>
      <h2 className={clsx('mt-3 animate-rise font-display text-emerald-300', inline ? 'text-xl' : 'text-3xl sm:text-4xl')} style={{ animationDelay: '1s' }}>
        Этой ночью никто не погиб
      </h2>
    </>
  );
}

function Lover({ guest, died, silenced, inline }: { guest: Person; died: boolean; silenced: boolean; inline: boolean }) {
  const size = inline ? 64 : 104;
  return (
    <>
      <p className={clsx('animate-rise font-display uppercase tracking-[.3em] text-pink-300', inline ? 'text-xs' : 'text-sm')}>Ночь любовницы</p>
      {/* Сверху запас под бабочку и сердечки — они не наезжают на подпись */}
      <div className={clsx('relative flex items-end justify-center', inline ? 'mb-5 mt-2 h-36 pb-2' : 'mb-10 mt-4 h-60 pb-4')}>
        {/* Сердечки над гостем */}
        {[0, 0.6, 1.2, 1.8].map((d, i) => (
          <span
            key={i}
            className="pointer-events-none absolute bottom-1/2 animate-heart-float text-pink-400 motion-reduce:hidden"
            style={{ left: `calc(50% + ${(i - 1.5) * 26}px)`, animationDelay: `${3 + d}s`, fontSize: inline ? 14 : 22 }}
            aria-hidden
          >
            ♥
          </span>
        ))}
        <div className={clsx('rounded-full', died ? 'animate-fade' : 'animate-glow')} style={died ? { animationDelay: '3.2s' } : undefined}>
          <Avatar id={guest.avatarId} name={guest.name} size={size} />
        </div>
        {/* Бабочка летит к гостю */}
        <span className={clsx('absolute animate-butterfly-path motion-reduce:animate-none', inline ? 'text-3xl' : 'text-5xl')} aria-hidden>
          <span className="inline-block animate-flap">🦋</span>
        </span>
      </div>
      <h2 className={clsx('animate-rise font-display text-zinc-100', inline ? 'text-xl' : 'text-3xl sm:text-4xl')} style={{ animationDelay: '2.6s' }}>
        Любовница провела ночь с №{guest.seat} {guest.name}
      </h2>
      <p className={clsx('mt-3 animate-rise', inline ? 'text-sm' : 'text-lg', died ? 'text-blood-400' : 'text-pink-200')} style={{ animationDelay: '3.2s' }}>
        {died ? 'К ней пришла мафия — гость погиб вместе с ней' : silenced ? 'Сегодня он молчит и не голосует' : ''}
      </p>
    </>
  );
}
